import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../components/ProductCard";

export default function Cart() {
  const { t } = useTranslation();
  const { items, setQuantity, remove, total } = useCart();

  return (
    <div data-testid="cart-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="overline mb-5">{t("cart.overline")}</div>
        <h1 className="font-serif text-5xl md:text-7xl text-[#F5F5F0] leading-[0.95] mb-16">
          {t("cart.title_1")} <em className="text-[#D4AF37] not-italic">{t("cart.title_2")}</em>
        </h1>

        {items.length === 0 ? (
          <div className="border-y border-white/10 py-20 text-center">
            <p className="text-[#A3A39D] mb-8">{t("cart.empty")}</p>
            <Link to="/boutique" className="btn-primary" data-testid="cart-empty-shop-btn">{t("cart.cta_shop")}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 border-t border-white/10">
              {items.map((i) => (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-5 py-6 border-b border-white/10">
                  <div className="w-28 h-36 bg-[#151516] shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-serif text-xl text-[#F5F5F0] mb-1">{i.name}</h3>
                      <div className="text-sm text-[#A3A39D]">{formatPrice(i.price)}</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-white/10">
                        <button onClick={() => setQuantity(i.product_id, i.quantity - 1)} className="p-2 hover:text-[#D4AF37]" data-testid={`decrease-${i.product_id}`}>
                          <Minus size={14} strokeWidth={1.25} />
                        </button>
                        <span className="px-4 text-sm tracking-widest">{i.quantity}</span>
                        <button onClick={() => setQuantity(i.product_id, i.quantity + 1)} className="p-2 hover:text-[#D4AF37]" data-testid={`increase-${i.product_id}`}>
                          <Plus size={14} strokeWidth={1.25} />
                        </button>
                      </div>
                      <button onClick={() => remove(i.product_id)} className="text-[#A3A39D] hover:text-[#D4AF37] inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid={`remove-${i.product_id}`}>
                        <X size={14} strokeWidth={1.25} /> {t("cart.remove")}
                      </button>
                    </div>
                  </div>
                  <div className="hidden md:block font-serif text-xl text-[#D4AF37] whitespace-nowrap">{formatPrice(i.price * i.quantity)}</div>
                </div>
              ))}
            </div>

            <aside className="bg-[#151516] p-8 h-fit border border-white/10">
              <div className="overline mb-5">{t("cart.summary")}</div>
              <div className="flex justify-between py-4 border-b border-white/10">
                <span className="text-[#A3A39D] text-sm">{t("cart.subtotal")}</span>
                <span className="font-serif text-xl text-[#F5F5F0]">{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between py-4 border-b border-white/10">
                <span className="text-[#A3A39D] text-sm">{t("cart.shipping")}</span>
                <span className="text-[#A3A39D] text-sm">{t("cart.to_estimate")}</span>
              </div>
              <div className="flex justify-between py-4 mb-6">
                <span className="text-[#F5F5F0] uppercase tracking-[0.18em] text-sm">{t("cart.total")}</span>
                <span data-testid="cart-total" className="font-serif text-3xl text-[#D4AF37]">{formatPrice(total)}</span>
              </div>
              <Link to="/commande" data-testid="checkout-btn" className="btn-primary w-full text-center block">
                {t("cart.checkout")}
              </Link>
              <p className="text-xs text-[#A3A39D] mt-6 leading-relaxed">{t("cart.note")}</p>
              <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#A3A39D]">
                <span className="text-[#D4AF37]">🔒 Stripe</span>
                <span>Visa · MC · AmEx</span>
                <span>SSL · 3D Secure</span>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
